import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleLabel, getUserTypeLabel, formatDateTime } from '../../utils/helpers';
import { User, Mail, Shield, Building2, Calendar, Edit, Save, X } from 'lucide-react';
import LoadingSpinner from '../Common/LoadingSpinner';
import toast from 'react-hot-toast';

const ProfileSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  currentPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters'),
  newPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
});

const Profile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    try {
      // Here you would typically call an API to update the user profile
      // For now, we'll just show a success message
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);
      const message = error.response?.data?.message || 'Failed to update profile. Please try again.';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600">Manage your account information and settings</p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className={`btn-secondary flex items-center space-x-2 ${
            isEditing ? 'bg-red-100 text-red-700 hover:bg-red-200' : ''
          }`}
        >
          {isEditing ? (
            <>
              <X className="h-4 w-4" />
              Cancel
            </>
          ) : (
            <>
              <Edit className="h-4 w-4" />
              Edit Profile
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
            
            <Formik
              initialValues={initialValues}
              validationSchema={ProfileSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label">User Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <Field
                          name="name"
                          type="text"
                          className={`input pl-10 ${!isEditing ? 'bg-gray-50' : ''} ${
                            errors.name && touched.name ? 'input-error' : ''
                          }`}
                          disabled={!isEditing}
                          placeholder="Enter your User Name"
                        />
                      </div>
                      <ErrorMessage name="name" component="p" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <label className="label">Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <Field
                          name="email"
                          type="email"
                          className={`input pl-10 ${!isEditing ? 'bg-gray-50' : ''} ${
                            errors.email && touched.email ? 'input-error' : ''
                          }`}
                          disabled={!isEditing}
                          placeholder="Enter your email address"
                        />
                      </div>
                      <ErrorMessage name="email" component="p" className="text-red-500 text-sm mt-1" />
                    </div>
                  </div>

                  {/* Password Change Section */}
                  {isEditing && (
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-md font-medium text-gray-900 mb-4">Change Password</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="label">Current Password</label>
                          <div className="relative">
                            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <Field
                              name="currentPassword"
                              type="password"
                              className="input pl-10"
                              placeholder="Enter current password"
                            />
                          </div>
                          <ErrorMessage name="currentPassword" component="p" className="text-red-500 text-sm mt-1" />
                        </div>

                        <div>
                          <label className="label">New Password</label>
                          <div className="relative">
                            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <Field
                              name="newPassword"
                              type="password"
                              className="input pl-10"
                              placeholder="Enter new password"
                            />
                          </div>
                          <ErrorMessage name="newPassword" component="p" className="text-red-500 text-sm mt-1" />
                        </div>

                        <div className="md:col-span-2">
                          <label className="label">Confirm New Password</label>
                          <div className="relative">
                            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <Field
                              name="confirmPassword"
                              type="password"
                              className="input pl-10"
                              placeholder="Confirm new password"
                            />
                          </div>
                          <ErrorMessage name="confirmPassword" component="p" className="text-red-500 text-sm mt-1" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  {isEditing && (
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="btn-secondary"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <LoadingSpinner size="sm" text="Saving..." />
                        ) : (
                          <>
                            <Save className="h-4 w-4" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Account Information */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Role</span>
                <span className="text-sm text-gray-900 capitalize">{getRoleLabel(user?.role)}</span>
              </div>
              
              {user?.userType && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">User Type</span>
                  <span className="text-sm text-gray-900">{getUserTypeLabel(user?.userType)}</span>
                </div>
              )}
              
              {user?.plant && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Plant</span>
                  <span className="text-sm text-gray-900">{user?.plantName || 'N/A'}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Status</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Member Since</span>
                <span className="text-sm text-gray-900">{formatDateTime(user?.createdAt)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">Last Updated</span>
                <span className="text-sm text-gray-900">{formatDateTime(user?.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-blue-500 mr-3" />
                  <span className="text-sm font-medium text-blue-900">Compliances</span>
                </div>
                <span className="text-lg font-bold text-blue-900">0</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-sm font-medium text-green-900">Due This Week</span>
                </div>
                <span className="text-lg font-bold text-green-900">0</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-yellow-500 mr-3" />
                  <span className="text-sm font-medium text-yellow-900">Pending</span>
                </div>
                <span className="text-lg font-bold text-yellow-900">0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 